import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Creatable } from 'components/controls/select';

function toOptions(values) {
  return (values || []).map((v) => ({ value: v, label: v }));
}

/**
 * GraphCreateDialog — modal to capture an element's type at creation time.
 *
 * Both fields are editable dropdowns (react-select Creatable) pre-populated with the labels already present in
 * the visible graph, so the user can pick an existing one or type a new value. Vertex labels are multi-select;
 * an edge takes a single label.
 *
 * Props:
 *   show         {boolean}    - whether the modal is visible
 *   mode         {"vertex"|"edge"} - which type of element is being created
 *   vertexLabels {string[]}   - distinct vertex labels in the visible graph (vertex mode options)
 *   edgeLabels   {string[]}   - distinct edge labels in the visible graph (edge mode options)
 *   onCancel     {Function}   - called when the user dismisses the dialog
 *   onCreate     {Function}   - vertex mode: called with { labels: string[], props: {} }
 *                               edge mode:   called with { label: string }
 */
class GraphCreateDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = { labelsValue: [], labelValue: null };
  }

  componentDidUpdate(prevProps) {
    // Reset the fields each time the dialog (re)opens.
    if (this.props.show && !prevProps.show) {
      this.setState({ labelsValue: [], labelValue: null });
    }
  }

  // Trimmed, empties-dropped list of the selected vertex labels.
  selectedVertexLabels() {
    return (this.state.labelsValue || []).map((o) => String(o.value).trim()).filter(Boolean);
  }

  handleSubmit() {
    const { mode, onCreate } = this.props;
    if (mode === "vertex") {
      const labels = this.selectedVertexLabels();
      if (labels.length === 0) return; // a vertex must be created with at least one label
      onCreate({ labels, props: {} });
    } else {
      const label = this.state.labelValue ? String(this.state.labelValue.value).trim() : "";
      if (!label) return;
      onCreate({ label });
    }
  }

  render() {
    const { show, mode, onCancel, vertexLabels, edgeLabels } = this.props;
    const isVertex = mode === "vertex";
    const canSubmit = isVertex
      ? this.selectedVertexLabels().length > 0
      : !!(this.state.labelValue && String(this.state.labelValue.value).trim());
    const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

    return (
      <Modal show={!!show} onHide={onCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isVertex ? "Add Vertex" : "Add Edge"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => { e.preventDefault(); if (canSubmit) this.handleSubmit(); }}>
            {isVertex ? (
              <Form.Group>
                <Form.Label><strong>Labels</strong></Form.Label>
                <Creatable
                  isMulti
                  autoFocus
                  placeholder="Select or create labels…"
                  options={toOptions(vertexLabels)}
                  value={this.state.labelsValue}
                  onChange={(opts) => this.setState({ labelsValue: opts || [] })}
                  menuPortalTarget={menuPortalTarget}                />
                <Form.Text className="text-muted">
                  Choose existing labels or type new ones. At least one is required — labels can't be
                  changed after the vertex is created.
                </Form.Text>
              </Form.Group>
            ) : (
              <Form.Group>
                <Form.Label><strong>Label</strong></Form.Label>
                <Creatable
                  autoFocus
                  placeholder="Select or create a label…"
                  options={toOptions(edgeLabels)}
                  value={this.state.labelValue}
                  onChange={(opt) => this.setState({ labelValue: opt })}
                  menuPortalTarget={menuPortalTarget}                />
                <Form.Text className="text-muted">
                  The relationship type for this edge. Required.
                </Form.Text>
              </Form.Group>
            )}
            <button type="submit" style={{ display: "none" }} />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onCancel}>Cancel</Button>
          <Button variant="success" disabled={!canSubmit} onClick={() => this.handleSubmit()}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default GraphCreateDialog;
